import { useEffect, useMemo, useState } from "react";
import { fetchNavbarItems } from "src/api/payload/navbar";
import { mapNavbarResponse } from "src/utils/mapNavbar";
import { navbarLivePreviewRequestHandler } from "src/utils/navbarLivePreviewHandler";
import { buildNavbarTree, mergeNavbarLiveDoc } from "src/utils/navbarTree";
import { useLivePreviewDoc } from "src/hooks/useLivePreviewDoc";

/**
 * Live Preview for Payload's document edit panel — full navbar tree with the
 * unsaved document merged in (same pattern as promotions card preview).
 * Runs regardless of REACT_APP_PAYLOAD_SOURCES so CMS editors can preview
 * before the public site navbar source is enabled.
 *
 * @param {string} locale - `en` | `fr`
 */
export function useNavbarLivePreview(locale) {
  const normalizedLocale = locale === "fr" ? "fr" : "en";
  const { liveDoc, isLoading: isLoadingLive } = useLivePreviewDoc(
    navbarLivePreviewRequestHandler,
    2,
  );

  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingDocs(true);
    setError(null);

    fetchNavbarItems(normalizedLocale)
      .then((items) => {
        if (!cancelled) setDocs(items);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error fetching navbar-items for live preview:", err);
          setError(err?.message || "Failed to load navbar items");
          setDocs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDocs(false);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedLocale]);

  const liveId = liveDoc?.id != null ? liveDoc.id : null;

  const links = useMemo(() => {
    const merged = mergeNavbarLiveDoc(docs, liveDoc);
    const tree = buildNavbarTree(merged, normalizedLocale, new Date(), {
      previewId: liveId,
    });
    return mapNavbarResponse(tree);
  }, [docs, liveDoc, normalizedLocale, liveId]);

  return {
    links,
    isResolved: true,
    isLoading: loadingDocs || isLoadingLive,
    isLivePreview: Boolean(liveId),
    livePreviewId: liveId ?? undefined,
    error,
  };
}
